import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { getTokenCoords, getPathCoords, getColor } from '../LudoConstants';

export default function Token({ token, playerIndex, gs, isPlayable, onClick, rollValue }) {
  const controls = useAnimation();
  const [coords, setCoords] = useState(getTokenCoords(token, playerIndex));
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const prevPos = useRef(token.main_track_pos);
  const prevState = useRef(token.state);
  const prevHomeStep = useRef(token.home_col_step);
  const lastRollValue = useRef(0);

  if (rollValue > 0) {
    lastRollValue.current = rollValue;
  }

  const animateMovement = useCallback(async () => {
    let path = [];
    const usedRoll = lastRollValue.current || 1;
    
    if (prevState.current === 'BASE' && token.state === 'ACTIVE') {
      path = [getTokenCoords(token, playerIndex)];
    } else {
      path = getPathCoords(
        prevState.current === 'HOME_COLUMN' ? prevHomeStep.current : prevPos.current,
        usedRoll,
        playerIndex,
        prevState.current === 'HOME_COLUMN'
      );
    }

    for (const step of path) {
      await controls.start({
        left: `${((step[0] + offset.x) / 15) * 100}%`,
        top: `${((step[1] + offset.y) / 15) * 100}%`,
        transition: { duration: 0.2, ease: "easeInOut" }
      });
    }
    setCoords(getTokenCoords(token, playerIndex));
  }, [controls, offset.x, offset.y, playerIndex, token]);

  useEffect(() => {
    // ... same sameSpotTokens logic ...
    const sameSpotTokens = [];
    const players = gs?.players || [];
    players.forEach((p, pIdx) => {
      p?.tokens?.forEach((t) => {
        if (t.state === token.state) {
          if (t.state === 'ACTIVE' && t.main_track_pos === token.main_track_pos) sameSpotTokens.push({pIdx, id: t.id});
          if (t.state === 'HOME_COLUMN' && t.home_col_step === token.home_col_step && pIdx === playerIndex) sameSpotTokens.push({pIdx, id: t.id});
        }
      });
    });

    if (sameSpotTokens.length > 1) {
      const myIdx = sameSpotTokens.findIndex(t => t.pIdx === playerIndex && t.id === token.id);
      if (myIdx !== -1) {
        const angle = (myIdx / sameSpotTokens.length) * Math.PI * 2;
        const radius = 0.3; 
        setOffset({ x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
      }
    } else {
      setOffset({ x: 0, y: 0 });
    }

    const isMoving = 
      (token.state === prevState.current && token.main_track_pos !== prevPos.current) ||
      (token.state === 'HOME_COLUMN' && token.home_col_step !== prevHomeStep.current) ||
      (token.state !== prevState.current && prevState.current !== null);

    if (isMoving) {
      animateMovement();
    } else {
      setCoords(getTokenCoords(token, playerIndex));
    }

    prevPos.current = token.main_track_pos;
    prevState.current = token.state;
    prevHomeStep.current = token.home_col_step;
  }, [token, gs, playerIndex, animateMovement]);

  const [x, y] = coords;
  const color = getColor(playerIndex);

  if (token.state === 'FINISHED') return null;

  return (
    <motion.div
      className={`ludo-token ${isPlayable ? 'playable' : ''}`}
      initial={false}
      animate={controls}
      style={{
        position: 'absolute',
        left: `${((x + offset.x) / 15) * 100}%`,
        top: `${((y + offset.y) / 15) * 100}%`,
        width: '6.5%',
        height: '6.5%',
        transform: 'translate(-50%, -50%)',
        zIndex: isPlayable ? 300 : 150,
        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.6))',
        cursor: isPlayable ? 'pointer' : 'default',
        pointerEvents: 'auto',
      }}
      onClick={isPlayable ? onClick : null}
    >
      <svg viewBox="-10 -10 120 120" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
        <circle cx="50" cy="50" r="45" fill={color} stroke="white" strokeWidth="5" />
        <circle cx="50" cy="50" r="25" fill="white" opacity="0.3" />
        {isPlayable && (
          <circle cx="50" cy="50" r="50" fill="none" stroke="white" strokeWidth="3">
            <animate attributeName="r" from="45" to="65" dur="1.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from="1" to="0" dur="1.5s" repeatCount="indefinite" />
          </circle>
        )}
      </svg>
    </motion.div>
  );
}
